import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, ArrowLeft } from 'lucide-react';

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '' });

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await api.get(`/invites/verify/${token}`);
        setInvite(res.data);
        setForm(p => ({ ...p, name: res.data.name || '' }));
      } catch (e) {
        setInvite(null);
      }
      setLoading(false);
    };
    verify();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/auth/accept-invite', { token, password: form.password, name: form.name });
      setDone(true);
      toast.success('Account created!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create account');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="bg-white rounded-md border border-black/[0.06] p-8 max-w-md w-full text-center">
          <p className="text-lg font-semibold text-[#0A0A0A] mb-2">Invalid or Expired Invite</p>
          <p className="text-sm text-[#52525B] mb-6">This invite link is no longer valid.</p>
          <Button onClick={() => navigate('/login')} variant="outline" className="rounded-md">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="bg-white rounded-md border border-black/[0.06] p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#0A0A0A] mb-2">Account Created</p>
          <p className="text-sm text-[#52525B] mb-6">You can now sign in with your email and password.</p>
          <Button onClick={() => navigate('/login')} className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md btn-premium">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" data-testid="invite-accept-page">
      <div className="bg-white rounded-md border border-black/[0.06] p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="font-heading text-xl font-semibold text-[#0A0A0A]">Paridhi Apparels</h1>
          <p className="text-[9px] tracking-[0.15em] uppercase text-[#C2A878] font-semibold mt-0.5">Manufacturing Portal</p>
        </div>
        <p className="text-sm text-[#52525B] text-center mb-6">
          You've been invited as <span className="font-semibold text-[#0A0A0A] capitalize">{invite.role?.replace('_', ' ')}</span>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Email</Label>
            <Input value={invite.email} disabled className="h-10 rounded-md bg-[#FAFAF8]" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Your Name *</Label>
            <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="h-10 rounded-md" data-testid="invite-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Set Password *</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" className="h-10 rounded-md" data-testid="invite-password-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Confirm Password *</Label>
            <Input type="password" value={form.confirmPassword} onChange={(e) => setForm(p => ({ ...p, confirmPassword: e.target.value }))} placeholder="Re-enter password" className="h-10 rounded-md" data-testid="invite-confirm-input" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="invite-submit-btn">
            {saving ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      </div>
    </div>
  );
}
